import { createInfiniteQuery } from 'react-query-kit';
import { keepPreviousData } from '@tanstack/react-query';

import type { QuizsetCursorSearchReq } from '@/api/gen/_model/quizsetCursorSearchReq';
import { isRecoverableCursorError } from '@/shared/hooks/useCursorQueryRecovery';

import { type LibrarySearchPage, searchLibrary } from './library.fetchers';
import { libraryKeys } from './library.keys';

export type LibrarySearchVariables = Omit<QuizsetCursorSearchReq, 'cursor'>;

const SEARCH_CACHE_STALE_TIME_MS = 5 * 60 * 1000;

/**
 * 첫 페이지만 자동 리페치한다. 스크롤을 한참 내린 뒤 탭을 다시 열었을 때 로드된 페이지를 전부
 * 다시 받아오면 사용자가 보던 위치가 흔들린다.
 */
const MAX_AUTOMATIC_REFETCH_PAGES = 1;

const shouldAutomaticallyRefetch = (query: { state: { data?: unknown } }) => {
  const data = query.state.data as { pages?: unknown[] } | undefined;
  return (data?.pages?.length ?? 0) <= MAX_AUTOMATIC_REFETCH_PAGES;
};

export const useSearchLibrary = createInfiniteQuery<
  LibrarySearchPage,
  LibrarySearchVariables,
  Error,
  string | undefined
>({
  queryKey: libraryKeys.search(),
  fetcher: (variables, { pageParam, signal }) =>
    searchLibrary({ ...variables, cursor: pageParam }, signal),
  staleTime: SEARCH_CACHE_STALE_TIME_MS,
  placeholderData: keepPreviousData,
  refetchOnMount: shouldAutomaticallyRefetch,
  refetchOnWindowFocus: shouldAutomaticallyRefetch,
  getNextPageParam: lastPage =>
    lastPage.hasNext ? lastPage.nextCursor ?? undefined : undefined,
  initialPageParam: undefined,
  // 커서 무효(배포로 형식 변경·만료·조건 불일치)는 화면을 깨지 않고 소비 훅이 첫 페이지부터 다시 읽는다.
  throwOnError: error => !isRecoverableCursorError(error),
});
